import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiLock, FiEye, FiDatabase, FiUserCheck, FiMail } = FiIcons;

const PrivacyPolicy = () => {
    const sections = [
        {
            title: 'Information We Collect',
            icon: FiDatabase,
            content: [
                'Personal information you provide when creating an account (name, email, phone number)',
                'Financial data you enter into our expense tracking system',
                'Usage data and analytics to improve our service',
                'Device and browser information for security purposes',
                'Communication records when you contact our support team'
            ]
        },
        {
            title: 'How We Use Your Information',
            icon: FiUserCheck,
            content: [
                'To provide and maintain our expense tracking services',
                'To process transactions and send related information',
                'To communicate with you about your account and our services',
                'To improve our platform and develop new features',
                'To detect and prevent fraud and security threats',
                'To comply with legal obligations and regulations'
            ]
        },
        {
            title: 'Data Security',
            icon: FiLock,
            content: [
                'All data is encrypted in transit and at rest using industry-standard encryption',
                'We use secure cloud infrastructure with regular security audits',
                'Access to your data is strictly limited to authorized personnel',
                'We implement multi-factor authentication for all admin accounts',
                'Regular security training for all employees handling user data',
                'Automated monitoring for suspicious activities and potential breaches'
            ]
        },
        {
            title: 'Information Sharing',
            icon: FiEye,
            content: [
                'We do not sell, trade, or rent your personal information to third parties',
                'We may share data with trusted service providers who assist in our operations',
                'Legal compliance: We may disclose information when required by law',
                'Business transfers: Information may be transferred in case of merger or acquisition',
                'With your consent: We may share information with your explicit permission',
                'Anonymized data may be used for research and analytics purposes'
            ]
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="text-center mb-12"
                >
                    <div className="flex justify-center mb-6">
                        <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl flex items-center justify-center">
                            <SafeIcon icon={FiShield} className="w-8 h-8 text-white" />
                        </div>
                    </div>
                    <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4">
                        Privacy Policy
                    </h1>
                    <p className="text-xl text-gray-600 dark:text-gray-300">
                        Your privacy is our priority. Learn how we collect, use, and protect your information.
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                        Last updated: January 15, 2024
                    </p>
                </motion.div>

                {/* Introduction */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 mb-8"
                >
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                        Our Commitment to Your Privacy
                    </h2>
                    <div className="prose prose-gray dark:prose-invert max-w-none">
                        <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                            At Softvative Inc, we are committed to protecting your privacy and ensuring the security
                            of your personal and financial information. This Privacy Policy explains how we collect,
                            use, disclose, and safeguard your information when you use our Expense Tracker SAAS platform.
                        </p>
                        <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                            By using our services, you agree to the collection and use of information in accordance
                            with this policy. We encourage you to read this policy carefully and contact us if you
                            have any questions.
                        </p>
                    </div>
                </motion.div>

                {/* Main Sections */}
                <div className="space-y-8">
                    {sections.map((section, index) => (
                        <motion.div
                            key={section.title}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.3 + index * 0.1 }}
                            className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8"
                        >
                            <div className="flex items-center mb-6">
                                <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center mr-4">
                                    <SafeIcon icon={section.icon} className="w-6 h-6 text-white" />
                                </div>
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                    {section.title}
                                </h2>
                            </div>

                            <ul className="space-y-3">
                                {section.content.map((item, itemIndex) => (
                                    <li key={itemIndex} className="flex items-start">
                                        <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                                        <span className="text-gray-600 dark:text-gray-300 leading-relaxed">
                                            {item}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>
                    ))}
                </div>

                {/* Additional Sections */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.8 }}
                        className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8"
                    >
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                            Your Rights
                        </h3>
                        <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                            <li>• Access your personal data</li>
                            <li>• Correct inaccurate information</li>
                            <li>• Request data deletion</li>
                            <li>• Data portability</li>
                            <li>• Withdraw consent</li>
                            <li>• Object to processing</li>
                        </ul>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.9 }}
                        className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8"
                    >
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                            Data Retention
                        </h3>
                        <div className="text-gray-600 dark:text-gray-300 space-y-2">
                            <p>We retain your information only as long as necessary to:</p>
                            <ul className="space-y-1 ml-4">
                                <li>• Provide our services</li>
                                <li>• Comply with legal obligations</li>
                                <li>• Resolve disputes</li>
                                <li>• Enforce our agreements</li>
                            </ul>
                        </div>
                    </motion.div>
                </div>

                {/* Contact Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 1.0 }}
                    className="bg-gradient-to-r from-primary-600 to-accent-600 rounded-2xl p-8 text-white mt-12"
                >
                    <div className="text-center">
                        <SafeIcon icon={FiMail} className="w-12 h-12 mx-auto mb-4" />
                        <h3 className="text-2xl font-bold mb-4">
                            Questions About Your Privacy?
                        </h3>
                        <p className="text-primary-100 mb-6 max-w-2xl mx-auto">
                            If you have any questions about this Privacy Policy or our data practices,
                            please don't hesitate to contact us. We're here to help and ensure your privacy is protected.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a
                                href="mailto:privacy@softvative.com"
                                className="inline-flex items-center justify-center px-6 py-3 bg-white text-primary-600 font-semibold rounded-xl hover:bg-gray-100 transition-colors duration-200"
                            >
                                <SafeIcon icon={FiMail} className="mr-2 w-5 h-5" />
                                privacy@softvative.com
                            </a>
                            <a
                                href="tel:+19524666048"
                                className="inline-flex items-center justify-center px-6 py-3 border-2 border-white text-white font-semibold rounded-xl hover:bg-white hover:text-primary-600 transition-all duration-200"
                            >
                                1-952-466-6048
                            </a>
                        </div>
                    </div>
                </motion.div>

                {/* Footer Note */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 1.1 }}
                    className="text-center mt-8 text-sm text-gray-500 dark:text-gray-400"
                >
                    <p>
                        This policy may be updated from time to time. We will notify you of any significant changes
                        via email or through our platform.
                    </p>
                </motion.div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;